// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a nudge card's button pressed in the Rail window. The Rail has no Konvo panel, so it
// hands the Konvo DM to the main window with what to open (`?open=nudge:…`, the Rail's usual
// hand-off parameter); the main window opens the panel there (connect step, or the thing to try).
// Also the booked setup-call slots and the slots still worth offering.

import type {Starter} from '@konvo/schema/src/domains/konvo/AssistantEngagement';

export const NUDGE_OPEN_PREFIX = 'nudge:';
const CONNECT = 'connect';
/** Route paths the desktop shell accepts stay under this length (context is dropped past it). */
const MAX_ROUTE_LENGTH = 1900;

/** What the panel opens with: the thing to try, or just the connect step (`starter: 'free'`, no request). */
export interface NudgePanelRequest {
	starter: Starter;
	request?: string;
	context?: Record<string, string>;
}

function isStringRecord(value: unknown): value is Record<string, string> {
	return (
		value != null &&
		typeof value === 'object' &&
		!Array.isArray(value) &&
		Object.values(value).every((entry) => typeof entry === 'string')
	);
}

/** The `open` value for a nudge hand-off. */
export function nudgeOpenValue(request: NudgePanelRequest | null): string {
	if (!request || (request.starter === 'free' && !request.request)) return `${NUDGE_OPEN_PREFIX}${CONNECT}`;
	const body: {s: Starter; r?: string; c?: Record<string, string>} = {s: request.starter};
	if (request.request) body.r = request.request;
	if (request.context && Object.keys(request.context).length > 0) body.c = request.context;
	return `${NUDGE_OPEN_PREFIX}${JSON.stringify(body)}`;
}

/** Reads a nudge `open` value; null when it is not one (or cannot be read). */
export function parseNudgeOpenValue(value: string | null | undefined): NudgePanelRequest | null {
	if (!value?.startsWith(NUDGE_OPEN_PREFIX)) return null;
	const rest = value.slice(NUDGE_OPEN_PREFIX.length);
	if (rest === CONNECT) return {starter: 'free'};
	try {
		const parsed = JSON.parse(rest) as {s?: unknown; r?: unknown; c?: unknown};
		if (typeof parsed.s !== 'string' || !parsed.s) return null;
		return {
			starter: parsed.s as Starter,
			...(typeof parsed.r === 'string' && parsed.r ? {request: parsed.r} : {}),
			...(isStringRecord(parsed.c) ? {context: parsed.c} : {}),
		};
	} catch {
		return null;
	}
}

/** The main-window route for a nudge hand-off: the Konvo DM, then the panel. */
export function nudgeHandoffRoute(dmPath: string, request: NudgePanelRequest | null, openParam = 'open'): string {
	const route = `${dmPath}?${openParam}=${encodeURIComponent(nudgeOpenValue(request))}`;
	if (route.length <= MAX_ROUTE_LENGTH || !request?.context) return route;
	// Too long for the hand-off: the request alone still runs (the panel asks for what it needs).
	return nudgeHandoffRoute(dmPath, {starter: request.starter, request: request.request}, openParam);
}

/** Setup-call slots still worth offering: those that have not started yet. */
export function upcomingSlots<T extends {start: string}>(slots: ReadonlyArray<T>, now: number): Array<T> {
	return slots.filter((slot) => {
		const at = Date.parse(slot.start);
		return Number.isFinite(at) && at > now;
	});
}
